<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { emailValidator, requiredValidator } from '@core/utils/validators'

// Addendum 13, Modul BD. Sama pola auth-check & dua lapis keamanan dengan
// halaman /platform lain -- lihat komentar di platform/tenants/index.vue.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const paymentMethodOptions = [
  { title: 'Xendit (otomatis)', value: 'xendit' },
  { title: 'Transfer Bank (manual)', value: 'bank_transfer' },
]

const form = ref({
  registration_payment_method: 'xendit',
  bank_name: '',
  bank_account_number: '',
  bank_account_holder: '',
  admin_whatsapp_number: '',
  support_email: '',
})

const formRef = ref()
const fetchLoading = ref(true)
const fetchError = ref('')
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchSettings = async () => {
  fetchLoading.value = true
  fetchError.value = ''
  try {
    const res = await usePlatformApi('/api/v1/platform/settings').get()
    const body = res?.data ?? res
    const s = body?.data ?? body

    form.value = {
      registration_payment_method: s.registration_payment_method ?? 'xendit',
      bank_name: s.bank_name ?? '',
      bank_account_number: s.bank_account_number ?? '',
      bank_account_holder: s.bank_account_holder ?? '',
      admin_whatsapp_number: s.admin_whatsapp_number ?? '',
      support_email: s.support_email ?? '',
    }
  } catch (err) {
    if (await handleAuthError(err))
      return

    fetchError.value = 'Gagal memuat pengaturan platform.'
    console.error('[Platform] fetch settings error', err)
  } finally {
    fetchLoading.value = false
  }
}

onMounted(fetchSettings)

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const res = await usePlatformApi('/api/v1/platform/settings').put({ body: form.value })
    const body = res?.data ?? res

    successMessage.value = body?.message ?? 'Pengaturan platform berhasil disimpan.'
  } catch (err) {
    if (await handleAuthError(err))
      return

    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      errors.value = { _general: [data?.message ?? 'Gagal menyimpan pengaturan platform.'] }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <h4 class="text-h4">
          Pengaturan Platform
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard max-width="720">
      <VCardTitle class="pt-5 px-5">
        Metode Pembayaran Registrasi
      </VCardTitle>
      <VCardSubtitle class="px-5 pb-2">
        Menentukan alur pembayaran yang dilihat pendaftar baru di halaman publik <code>/daftar</code>.
        Tidak berkaitan dengan pembayaran tenant (kasir/self-order), itu tetap terpisah.
      </VCardSubtitle>

      <VDivider />

      <!-- Loading state -->
      <VCardText
        v-if="fetchLoading"
        class="pa-10 text-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VCardText>

      <!-- Fetch error -->
      <VCardText
        v-else-if="fetchError"
        class="pa-5"
      >
        <VAlert
          type="error"
          variant="tonal"
        >
          {{ fetchError }}
        </VAlert>
      </VCardText>

      <VCardText
        v-else
        class="pa-5"
      >
        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          closable
          @click:close="successMessage = ''"
        >
          {{ successMessage }}
        </VAlert>

        <VAlert
          v-if="errors._general"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errors._general[0] }}
        </VAlert>

        <VForm
          ref="formRef"
          @submit.prevent="submit"
        >
          <VRow>
            <VCol cols="12">
              <VBtnToggle
                v-model="form.registration_payment_method"
                color="primary"
                divided
                mandatory
                class="payment-method-toggle"
              >
                <VBtn
                  v-for="opt in paymentMethodOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.title }}
                </VBtn>
              </VBtnToggle>
              <p
                v-if="errors.registration_payment_method"
                class="text-error text-caption mt-1 mb-0"
              >
                {{ errors.registration_payment_method[0] }}
              </p>
            </VCol>

            <VCol cols="12">
              <VAlert
                v-if="form.registration_payment_method === 'bank_transfer'"
                type="info"
                variant="tonal"
                density="compact"
              >
                Pendaftar baru akan diminta transfer manual ke rekening di bawah, lalu diverifikasi
                manual dari halaman "Persetujuan Pendaftar".
              </VAlert>
              <VAlert
                v-else
                type="info"
                variant="tonal"
                density="compact"
              >
                Pendaftar baru diarahkan ke invoice Xendit seperti alur yang sudah berjalan
                sekarang. Data rekening di bawah tetap bisa disiapkan lebih dulu sebelum dipakai.
              </VAlert>
            </VCol>

            <VCol cols="12">
              <h6 class="text-h6 mb-2">
                Rekening Tujuan Transfer
              </h6>
            </VCol>

            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.bank_name"
                label="Nama Bank"
                placeholder="mis. BCA"
                :error-messages="errors.bank_name"
              />
            </VCol>

            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.bank_account_number"
                label="Nomor Rekening"
                :error-messages="errors.bank_account_number"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.bank_account_holder"
                label="Nama Pemilik Rekening"
                :error-messages="errors.bank_account_holder"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.admin_whatsapp_number"
                label="Nomor WhatsApp Admin"
                placeholder="mis. 6281234567890 (format internasional, tanpa +/spasi)"
                hint="Dipakai untuk redirect wa.me notifikasi pendaftar baru setelah upload bukti transfer."
                persistent-hint
                :rules="form.registration_payment_method === 'bank_transfer' ? [requiredValidator] : []"
                :error-messages="errors.admin_whatsapp_number"
              />
            </VCol>

            <VCol cols="12">
              <h6 class="text-h6 mb-2">
                Kontak Dukungan
              </h6>
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.support_email"
                label="Email Support"
                placeholder="support@pintukasir.com"
                type="email"
                hint="Tujuan notifikasi email tiket dukungan baru dari tenant."
                persistent-hint
                :rules="[emailValidator]"
                :error-messages="errors.support_email"
              />
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-5">
        <VSpacer />
        <VBtn
          color="primary"
          :loading="loading"
          :disabled="fetchLoading || !!fetchError"
          @click="submit"
        >
          Simpan Perubahan
        </VBtn>
      </VCardActions>
    </VCard>
  </div>
</template>

<style scoped>
/*
 * Override template global (@core/.../_button.scss ".v-btn-toggle .v-btn")
 * yang memaksa SEMUA tombol di dalam VBtnToggle jadi kotak 36x36px !important
 * -- dirancang untuk toggle ikon-saja (mis. grid/list view switcher di POS),
 * bukan untuk tombol berlabel teks seperti "Xendit (otomatis)"/"Transfer
 * Bank (manual)" di sini. Sama pola dengan .billing-toggle di
 * pages/daftar/index.vue -- scoped ke halaman ini saja, TIDAK mengubah file
 * _button.scss template supaya toggle ikon lain di app tidak ikut berubah.
 */
.payment-method-toggle :deep(.v-btn) {
  inline-size: auto !important;
  padding-inline: 16px !important;
}
</style>
